import { formatDuration, parseDuration } from './scheduling.ts'

/**
 * A song's and an arrangement's details as their edit panels hold them: the
 * fields as typed (themes are the Themes tab's own editor, saved as they
 * change: docs/15), and what Save sends — only what changed, each value as
 * the API takes it — or why it can't yet. The song page reads the same song
 * for its header: who it is by, and one muted line of what else is known.
 */

// ---- the song ------------------------------------------------------------------------------

/** What the song's panel edits. */
export interface SongDetailsSource {
  title: string
  author: string | null
  artist: string | null
  ccliNumber: string | null
  copyright: string | null
  tempo: number | null
}

/** The panel's fields, as typed. */
export interface SongDetailsValues {
  title: string
  author: string
  artist: string
  ccliNumber: string
  copyright: string
  tempo: string
}

export interface SongDetailsPatch {
  title?: string
  author?: string | null
  artist?: string | null
  ccliNumber?: string | null
  copyright?: string | null
  tempo?: number | null
}

/** A field that can't be saved as it is, and why. */
export type DetailsProblems<K extends string> = Partial<Record<K, string>>

export function songDetailsValues(song: SongDetailsSource): SongDetailsValues {
  return {
    title: song.title,
    author: song.author ?? '',
    artist: song.artist ?? '',
    ccliNumber: song.ccliNumber ?? '',
    copyright: song.copyright ?? '',
    tempo: song.tempo === null ? '' : String(song.tempo),
  }
}

const text = (value: string): string | null => value.trim() || null

/** A whole number of beats a minute the API takes (20–300), null for none, undefined if neither. */
function bpm(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  return /^\d+$/.test(trimmed) && n >= 20 && n <= 300 ? n : undefined
}

/**
 * What Save sends for the song: the fields that changed, trimmed (blank is
 * none), and the problems that keep it from saving. Nothing changed: an
 * empty patch.
 */
export function songDetailsPatch(
  song: SongDetailsSource,
  values: SongDetailsValues,
): { patch: SongDetailsPatch; problems: DetailsProblems<keyof SongDetailsValues> } {
  const patch: SongDetailsPatch = {}
  const problems: DetailsProblems<keyof SongDetailsValues> = {}

  const title = values.title.trim()
  if (!title) problems.title = 'A song needs a title.'
  else if (title.length > 200) problems.title = 'At most 200 characters.'
  else if (title !== song.title) patch.title = title

  for (const field of ['author', 'artist'] as const) {
    const next = text(values[field])
    if (next && next.length > 200) problems[field] = 'At most 200 characters.'
    else if (next !== song[field]) patch[field] = next
  }

  const ccli = text(values.ccliNumber)
  if (ccli && !/^\d{1,10}$/.test(ccli)) problems.ccliNumber = 'Digits only, up to 10.'
  else if (ccli !== song.ccliNumber) patch.ccliNumber = ccli

  const copyright = text(values.copyright)
  if (copyright && copyright.length > 300) problems.copyright = 'At most 300 characters.'
  else if (copyright !== song.copyright) patch.copyright = copyright

  const tempo = bpm(values.tempo)
  if (tempo === undefined) problems.tempo = 'A whole number from 20 to 300.'
  else if (tempo !== song.tempo) patch.tempo = tempo

  return { patch, problems }
}

/**
 * The song page's header, under the title: who it is by (the artist, else
 * its author) and one muted line — who wrote it where that is someone else,
 * its tempo, how many arrangements. Each part once, never "Author unknown".
 */
export function songByline(
  song: Pick<SongDetailsSource, 'author' | 'artist' | 'tempo'>,
  arrangements: number,
): { by: string | null; meta: string[] } {
  const by = song.artist ?? song.author
  const wrote = song.author && song.author !== by ? `Written by ${song.author}` : null
  return {
    by,
    meta: [
      wrote,
      song.tempo !== null ? `${song.tempo} bpm` : null,
      arrangements > 1 ? `${arrangements} arrangements` : null,
    ].filter((part): part is string => part !== null),
  }
}

// ---- an arrangement ------------------------------------------------------------------------

export interface ArrangementSettingsSource {
  name: string
  key: string | null
  bpm: number | null
  meter: string | null
  lengthSeconds: number | null
}

export interface ArrangementSettingsValues {
  name: string
  /** '' for none. */
  key: string
  bpm: string
  /** '' for none. */
  meter: string
  /** "4:05" (or minutes: "5"). */
  length: string
}

export interface ArrangementSettingsPatch {
  name?: string
  key?: string | null
  bpm?: number | null
  meter?: string | null
  lengthSeconds?: number | null
}

export function arrangementSettingsValues(
  arrangement: ArrangementSettingsSource,
): ArrangementSettingsValues {
  return {
    name: arrangement.name,
    key: arrangement.key ?? '',
    bpm: arrangement.bpm === null ? '' : String(arrangement.bpm),
    meter: arrangement.meter ?? '',
    length: arrangement.lengthSeconds === null ? '' : formatDuration(arrangement.lengthSeconds),
  }
}

/** What Save sends for an arrangement's settings, and what keeps it from saving. */
export function arrangementSettingsPatch(
  arrangement: ArrangementSettingsSource,
  values: ArrangementSettingsValues,
): {
  patch: ArrangementSettingsPatch
  problems: DetailsProblems<keyof ArrangementSettingsValues>
} {
  const patch: ArrangementSettingsPatch = {}
  const problems: DetailsProblems<keyof ArrangementSettingsValues> = {}

  const name = values.name.trim()
  if (!name) problems.name = 'An arrangement needs a name.'
  else if (name.length > 120) problems.name = 'At most 120 characters.'
  else if (name !== arrangement.name) patch.name = name

  const key = values.key || null
  if (key !== arrangement.key) patch.key = key

  const beats = bpm(values.bpm)
  if (beats === undefined) problems.bpm = '20 to 300.'
  else if (beats !== arrangement.bpm) patch.bpm = beats

  const meter = values.meter || null
  if (meter !== arrangement.meter) patch.meter = meter

  const typed = values.length.trim()
  const seconds = typed === '' ? null : parseDuration(typed)
  if (typed !== '' && (seconds === null || seconds < 1 || seconds > 4 * 3600))
    problems.length = 'm:ss, up to 4 hours.'
  else if (seconds !== arrangement.lengthSeconds) patch.lengthSeconds = seconds

  return { patch, problems }
}
